/**
 * UnitCoverageTab — Admin > Units: the counties a unit covers (migration
 * 0185).
 *
 * One card per active unit, each with a county checkbox grid. Toggling a
 * checkbox saves immediately through PUT /admin/units/{id}/coverage (the
 * NotificationDefaultsTab / rate-bands-editor convention: no separate
 * Save button). Gate: manage_team, mirrored by the caller (AdminReview.tsx's
 * hideAiToolsTabs flag, the same gate the Rate bands editor and the
 * Notifications defaults tab share).
 *
 * The GET /units/coverage read returns each unit's counties as display
 * names (alphabetical, for the Team directory's quiet line); the option
 * list here comes from the county-typed service_areas lookup
 * (GET /lookups/service-areas?area_type=county, the same read the
 * provider service-area picker uses). A name collision between the two
 * reads is how a checkbox is shown checked -- county display names are
 * unique per the tenant-global lookup, so the match is unambiguous.
 */

import { useMemo, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';

/** The lookup pages at ten by default; every New Jersey county plus Pike, PA
 *  must be on offer, so the picker asks for the lot. */
export const COUNTY_PICKER_LIMIT = 100;
import { lookupsApi } from '@/lib/lookups';
import { useUnitCoverage, useSetUnitCoverage } from '@/lib/unitCoverage';
import { AsyncBoundary } from '@/components/primitives/AsyncBoundary';
import { useSupportContact } from '@/hooks/useSupportContact';
import { contactSentence } from '@/lib/errorCopy';

export interface UnitCoverageTabProps {
  tenantSlug: string;
}

export function UnitCoverageTab({ tenantSlug }: UnitCoverageTabProps): ReactNode {
  const contact = useSupportContact();
  const { units, isLoading: unitsLoading, isError: unitsError, error: unitsLoadError } = useUnitCoverage(tenantSlug);
  const countiesQuery = useQuery({
    queryKey: ['lookups', tenantSlug, 'service-areas', 'county'],
    queryFn: () => lookupsApi.listServiceAreas(tenantSlug, { area_type: 'county', limit: COUNTY_PICKER_LIMIT }),
  });
  const setCoverage = useSetUnitCoverage(tenantSlug);

  const counties = useMemo(
    () => (countiesQuery.data?.rows ?? []).slice().sort((a, b) => a.display_name.localeCompare(b.display_name)),
    [countiesQuery.data],
  );
  const idByName = useMemo(() => new Map(counties.map((c) => [c.display_name, c.id])), [counties]);

  function toggle(unitId: string, currentCounties: string[], countyId: string, countyName: string): void {
    const currentIds = new Set(
      currentCounties.map((name) => idByName.get(name)).filter((id): id is string => id !== undefined),
    );
    if (currentIds.has(countyId)) {
      currentIds.delete(countyId);
    } else {
      currentIds.add(countyId);
    }
    void countyName; // kept for readability at call sites; not needed once ids resolve
    setCoverage.mutate({ unitId, areaIds: Array.from(currentIds) });
  }

  return (
    <div>
      <p className="mb-4 text-sm text-ink-soft">
        The counties each unit covers.
      </p>

      <AsyncBoundary what="unit coverage"
        isLoading={unitsLoading || countiesQuery.isLoading}
        isError={unitsError || countiesQuery.isError}
        error={unitsError ? unitsLoadError : countiesQuery.error}
        onRetry={() => void countiesQuery.refetch()}
        emptyHeadline="No units yet"
      >
        <div className="flex flex-col gap-4">
          {units.map((unit) => (
            <div
              key={unit.unit_id}
              data-testid={`unit-coverage-card-${unit.unit_id}`}
              className="rounded-card border border-sage-800/[8%] bg-paper p-4"
            >
              <h3 className="font-display text-sm font-semibold text-sage-800">{unit.unit_name}</h3>
              <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-3 md:grid-cols-4">
                {counties.map((county) => {
                  const checked = unit.counties.includes(county.display_name);
                  return (
                    <label key={county.id} className="flex items-center gap-2 text-sm text-ink">
                      <input
                        type="checkbox"
                        data-testid={`unit-coverage-checkbox-${unit.unit_id}-${county.slug}`}
                        checked={checked}
                        disabled={setCoverage.isPending}
                        onChange={() => toggle(unit.unit_id, unit.counties, county.id, county.display_name)}
                        className="h-4 w-4 rounded border-sage-800/[25%] text-sage-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
                      />
                      {county.display_name}
                    </label>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </AsyncBoundary>

      {setCoverage.isError && (
        <p role="alert" className="mt-3 text-sm text-warm-700" data-testid="unit-coverage-error">
          {contactSentence(
            "That coverage change didn't save. Try again, and if it happens again, tell {contact_system}.",
            contact,
          )}
        </p>
      )}
    </div>
  );
}
